import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	Checkbox,
	TransparentButtonList
} from "client/Components";
import {
	SharePointSettingsNode,
	SharePointSettingsNodeId,
	SiteAvailabilityFilterKind
} from "client/Api/SharePointSettings/sharepointSettings";
import styles from "./HierarchySelection.module.less";
import { localize } from "client_legacy/ndaw/culture";
import {
	ensureNotNil,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import classNames from "classnames";
import { never } from "client_legacy/ndaw/errors";

export type HierarchySelectionProps = {
	rootNodeIds: readonly SharePointSettingsNodeId[];
	value: SharePointIdMap<UINode>;
	onChange: (value: SharePointIdMap<UINode>) => void;
	onReload: () => void;
	trackingId: string;
};

export const HierarchySelection = (props: HierarchySelectionProps): ReactElement => {
	const {
		rootNodeIds,
		value,
		onChange,
		onReload,
		trackingId
	} = props;

	const changeIsCheckedForAllNodes = useCallback(
		(isChecked: boolean): void => {
			const newMap = new SharePointIdMap<UINode>();

			const processNode = (nodeId: SharePointSettingsNodeId): void => {
				const node: UINode = ensureNotNil(value.get(nodeId));

				newMap.set(
					nodeId,
					{
						...node,
						isChecked
					}
				);

				for (const childNodeId of node.childIds) {
					processNode(childNodeId);
				}
			};

			for (const nodeId of rootNodeIds) {
				processNode(nodeId);
			}

			onChange(newMap);
		},
		[
			onChange,
			rootNodeIds,
			value
		]
	);

	const changeNodeHandler = useCallback(
		(node: UINode, isChecked: boolean): void => {
			const newMap = new SharePointIdMap<UINode>(value);

			const processNode = (nodeId: SharePointSettingsNodeId): void => {
				const currentNode: UINode = ensureNotNil(value.get(nodeId));

				newMap.set(
					nodeId,
					{
						...currentNode,
						isChecked
					}
				);

				for (const childNodeId of currentNode.childIds) {
					processNode(childNodeId);
				}
			};

			processNode(node.id);

			if (isChecked) {
				let parentId: SharePointSettingsNodeId | null = node.parentId;

				while (isNotNil(parentId)) {
					const currentNode: UINode = ensureNotNil(value.get(parentId));

					newMap.set(
						currentNode.id,
						{
							...currentNode,
							isChecked
						}
					);

					parentId = currentNode.parentId;
				}
			}

			onChange(newMap);
		},
		[
			onChange,
			value
		]
	);

	const selectAllHandler = useCallback(
		(): void => changeIsCheckedForAllNodes(true),
		[changeIsCheckedForAllNodes]
	);

	const deselectAllHandler = useCallback(
		(): void => changeIsCheckedForAllNodes(false),
		[changeIsCheckedForAllNodes]
	);

	return (
		<div {...setTrackingId(trackingId)}>
			<div className={styles.header}>
				<div className={styles.buttons}>
					<TransparentButtonList>
						<TransparentButtonList.Item
							onClick={selectAllHandler}
							text={localize("_SelectAll")}
							icon="check"
							trackingId="select-all"
						/>
						<TransparentButtonList.Item
							onClick={deselectAllHandler}
							text={localize("_DeselectAll")}
							icon="xmark"
							trackingId="deselect-all"
						/>
						<TransparentButtonList.Item
							onClick={onReload}
							text={localize("_Refresh")}
							icon="arrow-rotate-right"
							trackingId="refresh"
						/>
					</TransparentButtonList>
				</div>

				<span className={styles.checkboxMeaning}>
					{localize("_IsAvailable")}
				</span>
			</div>

			<NodeCollection
				ids={rootNodeIds}
				nodeMap={value}
				onNodeChange={changeNodeHandler}
				isVisible
			/>
		</div>
	);
};
HierarchySelection.displayName = "HierarchySelection";


export type NodeCollectionProps = {
	ids: readonly SharePointSettingsNodeId[];
	nodeMap: SharePointIdMap<UINode>;
	onNodeChange: (node: UINode, isChecked: boolean) => void;
	isVisible: boolean;
};

export const NodeCollection = (props: NodeCollectionProps): ReactElement | null => {
	const {
		ids,
		nodeMap,
		onNodeChange,
		isVisible
	} = props;

	const nodes = useMemo(
		(): readonly UINode[] => {
			return ids.map(x => ensureNotNil(nodeMap.get(x)));
		},
		[ids, nodeMap]
	);

	if (nodes.length === 0) {
		return null;
	}

	return (
		<ul
			className={classNames(
				styles.nodeCollection,
				{ [styles.nodeCollection_isVisible]: isVisible }
			)}
		>
			{nodes.map((x): ReactElement => (
				<Node
					key={`${x.id.siteCollectionId}:${x.id.objectId}`}
					node={x}
					nodeMap={nodeMap}
					onNodeChange={onNodeChange}
					isRoot={isNil(x.parentId)}
				/>
			))}
		</ul>
	);
};
NodeCollection.displayName = "NodeCollection";

export type NodeProps = {
	node: UINode;
	nodeMap: SharePointIdMap<UINode>;
	onNodeChange: (node: UINode, isChecked: boolean) => void;
	isRoot?: boolean;
};

export const Node = (props: NodeProps): ReactElement => {
	const {
		node,
		nodeMap,
		onNodeChange,
		isRoot = false
	} = props;

	return (
		<li>
			<div className={styles.nodeContent}>
				<span
					title={node.title}
					className={classNames(
						styles.nodeText,
						{ [styles.nodeText_root]: isRoot }
					)}
				>
					{node.title}
				</span>
				<div className={styles.checkboxColumn}>
					<Checkbox
						value={node.isChecked}
						onChange={() => onNodeChange(node, !node.isChecked)}
					/>
				</div>
			</div>
			<NodeCollection
				ids={node.childIds}
				nodeMap={nodeMap}
				onNodeChange={onNodeChange}
				isVisible={node.isChecked}
			/>
		</li>
	);
};
Node.displayName = "Node";

export class SharePointIdMap<TValue> {

	private readonly _obsoleteIds: Map<string, TValue>;
	private readonly _noneObsoleteIds: Map<string, TValue>;

	constructor(otherMap?: SharePointIdMap<TValue>) {
		if (isNil(otherMap)) {
			this._obsoleteIds = new Map<string, TValue>();
			this._noneObsoleteIds = new Map<string, TValue>();
		} else {
			this._obsoleteIds = new Map<string, TValue>(otherMap._obsoleteIds);
			this._noneObsoleteIds = new Map<string, TValue>(otherMap._noneObsoleteIds);
		}
	}

	public set(id: SharePointSettingsNodeId, value: TValue): void {
		const key: string = this.getKey(id);
		if (this.isObsolete(id)) {
			this._obsoleteIds.set(key, value);
		}
		else {
			this._noneObsoleteIds.set(key, value);
		}
	}

	public has(id: SharePointSettingsNodeId): boolean {
		if (this.isObsolete(id)) {
			throw new Error("Does not support obsolete Id's");
		}

		const key: string = this.getKey(id);
		if (this._noneObsoleteIds.has(key)) {
			return true;
		}
		if (this._obsoleteIds.has(key)) {
			return true;
		}

		return false;
	}

	public get(id: SharePointSettingsNodeId): TValue | undefined {
		if (this.isObsolete(id)) {
			throw new Error("Does not support obsolete Id's");
		}

		const key: string = this.getKey(id);
		if (this._noneObsoleteIds.has(key)) {
			return this._noneObsoleteIds.get(key);
		}
		if (this._obsoleteIds.has(key)) {
			return this._obsoleteIds.get(key);
		}

		return undefined;
	}

	private isObsolete(id: SharePointSettingsNodeId): boolean {
		return !id.siteCollectionId;
	}

	private getKey(id: SharePointSettingsNodeId): string {
		if (this.isObsolete(id)) {
			return id.objectId;
		}
		else {
			return id.siteCollectionId + "__" + id.objectId;
		}
	}
}

export type UINode = {
	id: SharePointSettingsNodeId;
	title: string;
	parentId: SharePointSettingsNodeId | null;
	isChecked: boolean;
	childIds: readonly SharePointSettingsNodeId[];
};

export function computeNodeMap(
	kind: Exclude<SiteAvailabilityFilterKind, SiteAvailabilityFilterKind.None>,
	nodeIds: readonly SharePointSettingsNodeId[],
	nodes: OptionalModel<readonly SharePointSettingsNode[]>
): SharePointIdMap<UINode> {
	const result = new SharePointIdMap<UINode>();

	if (!nodes.isSet) {
		return result;
	}

	const nodeIdsAsMap = new SharePointIdMap<null>();
	for (const nodeId of nodeIds) {
		nodeIdsAsMap.set(nodeId, null);
	}

	const processNode = (
		node: SharePointSettingsNode,
		parentId: SharePointSettingsNodeId | null
	): void => {

		const isParentChecked: boolean = isNil(parentId)
			// This is not really true, but for the logic it is ok. If we wanted it to be correct
			// we would have to negate the wording resulting in double negatives
			? true
			: ensureNotNil(
				result.get(parentId)
			)
				.isChecked;

		let isChecked: boolean = false;
		if (isParentChecked) {
			switch (kind) {
				case SiteAvailabilityFilterKind.Whitelist:
					isChecked = nodeIdsAsMap.has(node.id);
					break;
				case SiteAvailabilityFilterKind.Blacklist:
					isChecked = !nodeIdsAsMap.has(node.id);
					break;
				default:
					never(kind);
			}
		}

		const uiNode: UINode = {
			parentId,
			id: node.id,
			title: node.title,
			childIds: node.subItems.map(x => x.id),
			isChecked
		};

		result.set(uiNode.id, uiNode);

		for (const childNode of node.subItems) {
			processNode(childNode, uiNode.id);
		}
	};

	for (const node of nodes.value) {
		processNode(node, null);
	}

	return result;
}

export function computeNodeIds(
	rootNodeIds: readonly SharePointSettingsNodeId[],
	kind: Exclude<SiteAvailabilityFilterKind, SiteAvailabilityFilterKind.None>,
	nodeMap: SharePointIdMap<UINode>
): readonly SharePointSettingsNodeId[] {
	switch (kind) {
		case SiteAvailabilityFilterKind.Whitelist:
			return getWhitelistedNodeIdsForNodeContainer(rootNodeIds, nodeMap);
		case SiteAvailabilityFilterKind.Blacklist:
			return getBlacklistedNodeIdsForNodeContainer(rootNodeIds, nodeMap);
		default:
			never(kind);
	}
}

function getBlacklistedNodeIdsForNodeContainer(
	nodeIds: readonly SharePointSettingsNodeId[],
	map: SharePointIdMap<UINode>
): readonly SharePointSettingsNodeId[] {
	const result: SharePointSettingsNodeId[] = [];
	for (const nodeId of nodeIds) {
		result.push(...getBlacklistedNodeIdsForNode(nodeId, map));
	}
	return result;
}

function getBlacklistedNodeIdsForNode(
	nodeId: SharePointSettingsNodeId,
	map: SharePointIdMap<UINode>
): readonly SharePointSettingsNodeId[] {
	const node: UINode = ensureNotNil(map.get(nodeId));
	const isChecked: boolean = node.isChecked;

	if (!isChecked) {
		// We stop walking the hierarchy as when a parent is blacklisted, the state
		// of the children make no difference anymore
		return [nodeId];
	}

	return getBlacklistedNodeIdsForNodeContainer(node.childIds, map);
}

function getWhitelistedNodeIdsForNodeContainer(
	nodeIds: readonly SharePointSettingsNodeId[],
	map: SharePointIdMap<UINode>
): readonly SharePointSettingsNodeId[] {
	const result: SharePointSettingsNodeId[] = [];
	for (const nodeId of nodeIds) {
		result.push(...getWhitelistedNodeIdsForNode(nodeId, map));
	}
	return result;
}

function getWhitelistedNodeIdsForNode(
	nodeId: SharePointSettingsNodeId,
	map: SharePointIdMap<UINode>
): SharePointSettingsNodeId[] {
	const node: UINode = ensureNotNil(map.get(nodeId));
	const isChecked: boolean = node.isChecked;

	if (!isChecked) {
		// We stop walking the hierarchy as when a parent is NOT whitelisted, the state
		// of the children make no difference anymore
		return [];
	}

	const result: SharePointSettingsNodeId[] = [nodeId];

	result.push(...getWhitelistedNodeIdsForNodeContainer(node.childIds, map));
	return result;
}